import { money, percentText } from "../i18n/intl";
import { orderSourceLabel } from "./orders/orderBuild";
import type {
  AuthAuditLogEntry,
  AuthEvent,
  CatalogAuditAction,
  CatalogAuditEntry,
  CatalogAuditSource,
  OrderSource,
  ShopAuditAction,
  ShopAuditLogEntry,
} from "../api/types";

/**
 * G-17 label layer. The summaries are composed from the `metadata` each writer
 * actually stores — read out of the twelve `shopAuditLog.create` call sites,
 * not guessed. Where a writer stores nothing useful the summary is empty and
 * the row shows the action alone; that is correct, not a gap.
 *
 * Every lookup falls through to a readable default. A future enum value must
 * render as a humanised version of itself rather than crash the page, which is
 * why nothing here is typed as an exhaustive Record.
 */

const LABELS: Partial<Record<ShopAuditAction, string>> = {
  SHOP_MODE_ENTERED: "Kiosk armed",
  SHOP_MODE_EXITED: "Kiosk closed",
  PIN_VERIFY_SUCCESS: "PIN accepted",
  PIN_VERIFY_FAILURE: "PIN rejected",
  PIN_ESCALATION_BLOCKED: "Higher-role PIN blocked by the tablet ceiling",
  SALE_CREATED: "Sale rung up",
  SALE_DISPATCHED: "Sale sent with a courier",
  WASTE_LOGGED: "Waste logged",
  STOCK_VIEWED: "Stock looked up",
  STOCK_ADJUSTED: "Stock adjusted",
  SHOP_IDLE_LOCK: "Locked when idle",
  SHOP_BOUQUET_BUILT: "Bouquet built",
  TASK_CREATED: "Task created",
  TASK_ASSIGNED: "Task assigned",
  TASK_COMPLETED: "Task completed",
  TASK_CANCELLED: "Task cancelled",
  DELIVERY_LINE_CONFIRMED: "Delivery line received",
  SHOPIFY_ORDER_ACCEPTED: "Shopify order claimed",
  SHOPIFY_ORDER_PREPARED: "Shopify line prepared",
  SHOPIFY_ORDER_SOLD: "Shopify order sold",
  SHOPIFY_ORDER_RELEASED: "Shopify order released",
  ORDER_PREPARATION_STARTED: "Order preparation started",
  ORDER_MARKED_READY: "Order marked ready",
  ORDER_BOUQUET_BUILT: "Order bouquet built",
  ORDER_ITEM_PICKED: "Order item picked",
  ORDER_ITEM_UNPICKED: "Order item un-picked",
  SHIFT_CLOCKED_IN: "Clocked in",
  SHIFT_STARTED_LATE: "Started late",
  SHIFT_STARTED_EARLY: "Arrived early",
  NO_SHIFT_DAY: "Worked without a scheduled shift",
  SHIFT_CLOCKED_OUT: "Clocked out",
  COURIER_ARRIVAL_CONFIRMED: "Courier arrival confirmed",
  TASK_SCHEDULE_CREATED: "Recurring task created",
  TASK_SCHEDULE_UPDATED: "Recurring task updated",
  TASK_SCHEDULE_DELETED: "Recurring task deleted",
  BOUQUET_PHOTO_ATTACHED: "Bouquet photo added",
  ORDER_PHOTO_ATTACHED: "Reference photo added",
  SALE_BELOW_FLOOR: "Sold below the floor price",
  ACTION_UNDONE: "Action undone",
  // OD-32: a handover charged a different price than agreed — normal
  // business, made visible.
  ORDER_AMENDED_AT_SALE: "Order price changed at handover",
  // G-112 (OD-99): money recorded at the counter before the goods left.
  ORDER_PAYMENT_TAKEN: "Order payment taken",
  // G-84 storage boxes. "Box" rather than "storage box" in the label because
  // the code (BOX-000017) follows it in every summary below.
  BOX_ISSUED: "Box issued",
  BOX_RETIRED: "Box retired",
  BOX_EDITED: "Box edited",
  BOX_STOCK_MOVED_IN: "Stock put in a box",
  BOX_STOCK_MOVED_OUT: "Stock taken out of a box",
  // OD-47: the answer to "Is this from box X?", which is the whole reason the
  // question is asked — the label says who was asked, the summary says what
  // they said.
  BOX_PICK_ANSWERED: "Box question answered",
  BOX_VIEWED: "Box opened",
  BOX_LABEL_PRINTED: "Box label printed",
};

/** "ORDER_MARKED_READY" → "Order marked ready" for anything unmapped. */
function humanize(action: string): string {
  const words = action.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function actionLabel(action: ShopAuditAction): string {
  return LABELS[action] ?? humanize(action);
}

/** Rows worth spotting at a glance when scanning a day. */
export type ActionTone = "danger" | "warning" | "neutral";

/**
 * G-18 presence badges for one floor row. FAR is the red flag the owner
 * scans for; UNKNOWN (no usable coordinates) is a flag too, not a pass
 * (owner decision 4); "No shift today" is decision 6. Empty array = clean
 * row or pre-G-18 row (geoStatus null).
 */
export function presenceBadges(
  entry: ShopAuditLogEntry,
): Array<{ label: string; tone: ActionTone }> {
  const badges: Array<{ label: string; tone: ActionTone }> = [];
  if (entry.geoStatus === "FAR") {
    badges.push({
      label:
        entry.geoDistanceM != null
          ? `${entry.geoDistanceM} m from the shop`
          : "Far from the shop",
      tone: "danger",
    });
  } else if (entry.geoStatus === "UNKNOWN") {
    badges.push({ label: "Location unknown", tone: "warning" });
  }
  // G-50: recomputed on read — a manager adding the missing shift clears it.
  if (entry.unscheduled) {
    badges.push({ label: "No shift today", tone: "warning" });
  }
  return badges;
}

/** G-51: the actions whose domain effect the log can reverse. */
export const UNDOABLE_ACTIONS: ReadonlySet<ShopAuditAction> = new Set([
  "SALE_CREATED",
  "WASTE_LOGGED",
  "STOCK_ADJUSTED",
  "SHOP_BOUQUET_BUILT",
] as ShopAuditAction[]);

/**
 * G-151: the sale an undo row reversed. The sale is VOIDED, never deleted,
 * so the undo row can open it in Sales records. Null for any other row.
 */
export function undoneSaleId(entry: ShopAuditLogEntry): string | null {
  if (entry.action !== "ACTION_UNDONE" || entry.entityType !== "Sale") return null;
  const m = entry.metadata;
  const reversed =
    m && typeof m === "object" && !Array.isArray(m)
      ? (m as Record<string, unknown>).reversedAction
      : null;
  return reversed === "SALE_CREATED" ? entry.entityId : null;
}

/** A red arming taints every row of the session (G-18 amendment 8). */
export function sessionFlagged(entry: ShopAuditLogEntry): boolean {
  return (
    entry.sessionGeoStatus === "FAR" || entry.sessionGeoStatus === "UNKNOWN"
  );
}

/** The expanded row's "Location" line — honest about what was measured. */
export function presenceDetail(entry: ShopAuditLogEntry): string | null {
  if (!entry.geoStatus) return null;
  if (entry.geoStatus === "UNKNOWN") {
    return "The tablet sent no usable location for this action.";
  }
  const distance =
    entry.geoDistanceM != null ? `${entry.geoDistanceM} m from the shop` : "";
  const accuracy =
    entry.geoAccuracyM != null ? ` (accuracy ±${entry.geoAccuracyM} m)` : "";
  return entry.geoStatus === "FAR"
    ? `FAR — ${distance || "beyond 150 m"}${accuracy}`
    : `At the shop — ${distance || "within 150 m"}${accuracy}`;
}

export function actionTone(action: ShopAuditAction): ActionTone {
  if (action === "PIN_VERIFY_FAILURE" || action === "PIN_ESCALATION_BLOCKED") {
    return "danger";
  }
  // G-50 decision 1: a late arrival is the red row an owner scans for; an
  // early one is neutral by the same ruling.
  if (action === "SHIFT_STARTED_LATE") return "danger";
  if (action === "SALE_BELOW_FLOOR") return "warning";
  if (action === "ACTION_UNDONE") return "warning";
  if (
    action === "STOCK_ADJUSTED" ||
    action === "SHOP_IDLE_LOCK" ||
    action === "NO_SHIFT_DAY"
  ) {
    return "warning";
  }
  return "neutral";
}

/** The <optgroup> buckets of the action filter (owner decision 3). */
export const ACTION_GROUPS: Array<{
  label: string;
  actions: ShopAuditAction[];
}> = [
  {
    label: "Security",
    actions: [
      "PIN_VERIFY_SUCCESS",
      "PIN_VERIFY_FAILURE",
      "PIN_ESCALATION_BLOCKED",
      "SHOP_MODE_ENTERED",
      "SHOP_MODE_EXITED",
      "SHOP_IDLE_LOCK",
      "ACTION_UNDONE",
    ],
  },
  {
    label: "Sales",
    actions: ["SALE_CREATED", "SALE_DISPATCHED", "SALE_BELOW_FLOOR"],
  },
  {
    label: "Stock",
    actions: ["STOCK_VIEWED", "STOCK_ADJUSTED", "WASTE_LOGGED", "DELIVERY_LINE_CONFIRMED"],
  },
  {
    label: "Orders",
    actions: [
      "ORDER_PREPARATION_STARTED",
      "ORDER_MARKED_READY",
      "ORDER_BOUQUET_BUILT",
      "ORDER_ITEM_PICKED",
      "ORDER_ITEM_UNPICKED",
      "COURIER_ARRIVAL_CONFIRMED",
      "ORDER_PHOTO_ATTACHED",
      "ORDER_PAYMENT_TAKEN",
    ],
  },
  {
    label: "Bouquets",
    actions: ["SHOP_BOUQUET_BUILT", "BOUQUET_PHOTO_ATTACHED"],
  },
  {
    label: "Shopify",
    actions: [
      "SHOPIFY_ORDER_ACCEPTED",
      "SHOPIFY_ORDER_PREPARED",
      "SHOPIFY_ORDER_SOLD",
      "SHOPIFY_ORDER_RELEASED",
    ],
  },
  {
    label: "Tasks",
    actions: [
      "TASK_CREATED",
      "TASK_ASSIGNED",
      "TASK_COMPLETED",
      "TASK_CANCELLED",
      "TASK_SCHEDULE_CREATED",
      "TASK_SCHEDULE_UPDATED",
      "TASK_SCHEDULE_DELETED",
    ],
  },
  {
    label: "Shifts",
    actions: [
      "SHIFT_CLOCKED_IN",
      "SHIFT_STARTED_LATE",
      "SHIFT_STARTED_EARLY",
      "NO_SHIFT_DAY",
      "SHIFT_CLOCKED_OUT",
    ],
  },
];

function meta(entry: ShopAuditLogEntry): Record<string, unknown> {
  const raw = entry.metadata;
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

/** "1 unit" not "1 units" — these read as sentences in the row. */
function plural(count: string, singular: string): string {
  return `${count} ${count === "1" ? singular : `${singular}s`}`;
}

/**
 * G-141: "cash", or "cash €20.00 + card €20.00" for a split. Rows written
 * before G-141 carry a single `paymentType` instead.
 */
function salePaymentText(m: Record<string, unknown>): string | null {
  const rows = Array.isArray(m.payments)
    ? m.payments.flatMap((p: unknown) => {
        const row = p && typeof p === "object" ? (p as Record<string, unknown>) : {};
        const method = str(row.method);
        return method ? [{ method: method.toLowerCase(), amount: str(row.amountEur) }] : [];
      })
    : [];
  if (rows.length === 1) return rows[0].method;
  if (rows.length > 1) {
    return rows.map((r) => (r.amount ? `${r.method} €${r.amount}` : r.method)).join(" + ");
  }
  return str(m.paymentType)?.toLowerCase() ?? null;
}

function str(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number") return String(value);
  return null;
}

/**
 * The human sentence for a row, from that writer's own metadata keys. Returns
 * "" when the writer stored nothing worth showing (e.g. clock-in, whose
 * timestamps are already the row's time).
 */
export function actionSummary(entry: ShopAuditLogEntry): string {
  const m = meta(entry);
  const parts: string[] = [];

  switch (entry.action) {
    // G-50: "Started late · 33 min" — the minutes ride the row's metadata.
    case "SHIFT_STARTED_LATE": {
      const minutes = str(m.minutesLate);
      if (minutes) parts.push(`${minutes} min after planned start`);
      break;
    }
    case "SHIFT_STARTED_EARLY": {
      const minutes = str(m.minutesEarly);
      if (minutes) parts.push(`${minutes} min before planned start`);
      break;
    }
    case "SALE_CREATED": {
      const total = str(m.totalEur);
      const lines = str(m.lineCount);
      if (total) parts.push(`€${total}`);
      if (lines) parts.push(plural(lines, "line"));
      const payment = salePaymentText(m);
      if (payment) parts.push(payment);
      break;
    }
    case "SALE_DISPATCHED": {
      const order = str(m.orderCode);
      const job = str(m.jobCode);
      const price = str(m.priceEur);
      if (order) parts.push(order);
      if (job) parts.push(job);
      if (price) parts.push(`€${price} delivery`);
      break;
    }
    case "WASTE_LOGGED": {
      const qty = str(m.quantity);
      const cause = str(m.wasteCause);
      if (qty) parts.push(plural(qty, "unit"));
      if (cause) parts.push(cause.toLowerCase().replace(/_/g, " "));
      break;
    }
    /*
     * G-84 boxes. Every key below is read off the writers themselves
     * (`shop-mode-boxes.controller.ts` and `box-pick-audit.ts`), per this
     * file's rule: composed from stored metadata, never guessed.
     */
    case "BOX_ISSUED": {
      const code = str(m.code);
      const name = str(m.name);
      const keptIn = str(m.keptIn);
      if (code) parts.push(code);
      if (name) parts.push(`“${name}”`);
      if (keptIn) parts.push(`kept in ${keptIn}`);
      break;
    }
    case "BOX_RETIRED":
    case "BOX_LABEL_PRINTED":
    case "BOX_EDITED": {
      const code = str(m.code);
      const name = str(m.name);
      if (code) parts.push(code);
      if (name) parts.push(`“${name}”`);
      break;
    }
    case "BOX_VIEWED": {
      const code = str(m.code);
      const items = str(m.itemCount);
      if (code) parts.push(code);
      if (items) parts.push(plural(items, "item"));
      break;
    }
    case "BOX_STOCK_MOVED_IN":
    case "BOX_STOCK_MOVED_OUT": {
      const code = str(m.code);
      const qty = str(m.quantity);
      const item = str(m.itemName);
      const toZone = str(m.toZoneName);
      if (qty && item) parts.push(`${plural(qty, "unit")} of ${item}`);
      else if (item) parts.push(item);
      if (code) parts.push(entry.action === "BOX_STOCK_MOVED_IN" ? `into ${code}` : `out of ${code}`);
      if (toZone) parts.push(`onto ${toZone}`);
      break;
    }
    case "BOX_PICK_ANSWERED": {
      const code = str(m.code);
      // The answer is a boolean, so `str` would drop `false` — and "No" is
      // the answer that matters most here: it is the one that leaves the box
      // untouched and can refuse the sale.
      const taken = m.takenFromBox;
      if (typeof taken === "boolean") {
        parts.push(taken ? `yes, taken from ${code || "the box"}` : `no, not from ${code || "the box"}`);
      } else if (code) {
        parts.push(code);
      }
      break;
    }
    case "STOCK_ADJUSTED": {
      const qty = str(m.quantity);
      const invoice = str(m.invoiceTotalEur);
      if (qty) parts.push(plural(qty, "unit"));
      // G-121 S3: a priced receive since S3 stores the net and its IVA;
      // older rows only the typed figure, whose side of IVA is unknown.
      const iva = str(m.ivaEur);
      const rate = str(m.ivaRatePercent);
      if (invoice && str(m.moneyBasis) && iva && rate) {
        parts.push(`${money(invoice)} before IVA · IVA ${money(iva)} (${percentText(rate)} %)`);
      } else if (invoice) {
        parts.push(`€${invoice} invoiced`);
      }
      break;
    }
    case "SHOP_BOUQUET_BUILT": {
      const lines = str(m.lineCount);
      const paper = str(m.paperCount);
      if (lines) parts.push(plural(lines, "stem"));
      if (paper && paper !== "0") parts.push(plural(paper, "wrap"));
      break;
    }
    case "ORDER_PREPARATION_STARTED":
    case "ORDER_MARKED_READY":
    case "ORDER_BOUQUET_BUILT": {
      const code = str(m.code);
      const fulfillment = str(m.fulfillment);
      if (code) parts.push(code);
      if (fulfillment) parts.push(fulfillment.toLowerCase().replace(/_/g, " "));
      break;
    }
    case "ORDER_PAYMENT_TAKEN": {
      const code = str(m.code);
      const amount = str(m.amountEur);
      const method = str(m.method);
      const left = str(m.balanceDueEur);
      if (code) parts.push(code);
      if (amount) parts.push(`€${amount}`);
      if (method) parts.push(method.toLowerCase().replace(/_/g, " "));
      if (left && Number(left) > 0) parts.push(`€${left} still owed`);
      break;
    }
    case "ORDER_AMENDED_AT_SALE": {
      const code = str(m.code);
      const agreed = str(m.agreedTotalEur);
      const charged = str(m.chargedTotalEur);
      if (code) parts.push(code);
      parts.push(`agreed ${agreed || "—"} € → charged ${charged || "—"} €`);
      break;
    }
    case "DELIVERY_LINE_CONFIRMED": {
      const received = str(m.receivedQty);
      const expected = str(m.expectedQty);
      if (received && expected) parts.push(`${received} of ${expected} received`);
      const shortage = str(m.shortageQty);
      if (shortage) parts.push(`${shortage} short`);
      // G-121 S2: units that arrived damaged, and the value of what the paper
      // billed but the shop did not receive — information, not a receivable.
      const damaged = str(m.damagedQty);
      if (damaged && Number(damaged) > 0) parts.push(`${damaged} damaged`);
      const shortageEur = str(m.shortageEur);
      if (shortageEur && Number(shortageEur) > 0) parts.push(`not received €${shortageEur}`);
      break;
    }
    case "TASK_CANCELLED":
    case "SHOPIFY_ORDER_RELEASED": {
      const reason = str(m.reason);
      if (reason) parts.push(reason);
      break;
    }
    case "TASK_COMPLETED": {
      if (m.hasCompletionImage === true) parts.push("with photo");
      break;
    }
    case "TASK_SCHEDULE_CREATED":
    case "TASK_SCHEDULE_UPDATED":
    case "TASK_SCHEDULE_DELETED": {
      const name = str(m.name);
      if (name) parts.push(name);
      break;
    }
    case "COURIER_ARRIVAL_CONFIRMED": {
      const job = str(m.jobCode);
      if (job) parts.push(job);
      break;
    }
    default:
      break;
  }

  return parts.join(" · ");
}

// ── Security tab (account events) ────────────────────────────────────────────

/**
 * Labels for the account-plane events shown in the Security tab. Separate from
 * the floor labels above because they answer a different question: the floor
 * log says what happened IN A SHOP, this says what happened to an ACCOUNT.
 */
const AUTH_EVENT_LABELS: Partial<Record<AuthEvent, string>> = {
  LOGIN_SUCCESS: "Signed in",
  LOGIN_FAILURE: "Failed sign-in",
  REFRESH: "Session renewed",
  LOGOUT: "Signed out",
  LOCKED: "Account locked",
  PIN_SET: "New PIN issued",
  PIN_VIEWED: "PIN viewed",
  FOB_ISSUED: "Fob issued",
  FOB_REVOKED: "Fob revoked",
  PASSWORD_RESET: "Password reset",
  INVITE_ISSUED: "Invitation sent",
  INVITE_CANCELLED: "Invitation cancelled",
  ACCOUNT_SETUP_COMPLETED: "Account set up",
  EMAIL_VERIFIED: "Email confirmed",
  EMAIL_CHANGE_REQUESTED: "Email change asked",
  EMAIL_CHANGED: "Email changed",
  AI_INSTRUCTIONS_PUBLISHED: "AI instructions published",
  AI_INSTRUCTIONS_ROLLED_BACK: "AI instructions rolled back",
  PAYROLL_PAYMENT_RECORDED: "Pay recorded",
  PAYROLL_PAYMENT_DELETED: "Pay record deleted",
  PAYROLL_ADJUSTMENT_RECORDED: "Bonus or deduction recorded",
  PAYROLL_ADJUSTMENT_DELETED: "Bonus or deduction deleted",
  PAY_TERMS_CHANGED: "Pay terms changed",
  // G-170: pay rules.
  COMMISSION_RULE_CHANGED: "Commission switched",
  COURIER_PAYOUT_RULE_CREATED: "Courier pay rule created",
  COURIER_PAYOUT_RULE_CHANGED: "Courier pay rule changed",
};

/** G-170: the fields a courier pay rule's audit row can name. */
const PAYOUT_RULE_FIELDS: Record<string, string> = {
  name: "name",
  isDefault: "default",
  isActive: "active",
};
const PAYOUT_RULE_KINDS: Record<string, string> = {
  PERCENT: "percent of the fee",
  FLAT: "flat",
  PER_KM: "per km",
};

function onOff(value: unknown): string {
  return value === true ? "on" : value === false ? "off" : "—";
}

/** G-163: the pay-terms fields a PAY_TERMS_CHANGED row can name. */
const PAY_TERM_FIELDS: Record<string, { label: string; unit: "eur" | "percent" | "day" }> = {
  monthlySalaryEur: { label: "salary", unit: "eur" },
  hourlyRateEur: { label: "hourly rate", unit: "eur" },
  salesCommissionPercent: { label: "commission", unit: "percent" },
  payDayOfMonth: { label: "pay day", unit: "day" },
};

function payTermValue(value: unknown, unit: "eur" | "percent" | "day"): string {
  if (value === null || value === undefined) return "none";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (unit === "eur") return `€${n.toFixed(2)}`;
  if (unit === "percent") return `${n} %`;
  return String(n);
}

/** "2026-11" — the booked month or the month a salary starts. */
function payMonth(year: unknown, month: unknown): string | null {
  return typeof year === "number" && typeof month === "number" ? `${year}-${String(month).padStart(2, "0")}` : null;
}

/** G-149: the AI systems AI control can change, by their ledger key. */
const AI_SYSTEM_NAMES: Record<string, string> = { ITEM_PHOTO_SEARCH: "Photo search" };

export function authEventLabel(event: AuthEvent): string {
  return AUTH_EVENT_LABELS[event] ?? humanize(event);
}

/** A failed sign-in or a lockout is what an attack looks like — make it red. */
export function authEventTone(event: AuthEvent): ActionTone {
  if (event === "LOGIN_FAILURE" || event === "LOCKED") return "danger";
  if (
    event === "PIN_VIEWED" ||
    event === "PIN_SET" ||
    event === "PASSWORD_RESET" ||
    event === "EMAIL_CHANGED" ||
    event === "AI_INSTRUCTIONS_PUBLISHED" ||
    event === "AI_INSTRUCTIONS_ROLLED_BACK" ||
    event === "PAYROLL_PAYMENT_DELETED" ||
    event === "PAYROLL_ADJUSTMENT_DELETED" ||
    event === "PAY_TERMS_CHANGED" ||
    event === "COMMISSION_RULE_CHANGED" ||
    event === "COURIER_PAYOUT_RULE_CHANGED"
  ) {
    return "warning";
  }
  return "neutral";
}

/**
 * The human sentence for a security row. `names` maps user id → full name so
 * "X viewed Y's PIN" reads as people rather than UUIDs; an id with no match
 * (deleted user) falls back to "someone" rather than printing a raw UUID.
 */
export function authEventSummary(
  entry: AuthAuditLogEntry,
  names: Map<string, string>,
): string {
  const m =
    entry.metadata && typeof entry.metadata === "object" && !Array.isArray(entry.metadata)
      ? (entry.metadata as Record<string, unknown>)
      : {};
  const who = (value: unknown): string =>
    typeof value === "string" ? (names.get(value) ?? "someone") : "someone";

  switch (entry.event) {
    case "PIN_VIEWED":
      return `viewed by ${who(m.viewedByUserId)}`;
    case "PIN_SET":
      return `issued by ${who(m.setByUserId)}`;
    case "LOGIN_FAILURE":
      return entry.email ? `attempted as ${entry.email}` : "";
    // G-136 — who started it, and (for an address) what it became.
    case "INVITE_ISSUED":
      return `${m.resend === true ? "re-sent" : "sent"} by ${who(m.invitedByUserId)}`;
    case "INVITE_CANCELLED":
      return `by ${who(m.cancelledByUserId)}`;
    case "PASSWORD_RESET":
      // A reset link the person asked for themselves carries no requester.
      return m.via === "email"
        ? typeof m.requestedByUserId === "string"
          ? `link sent by ${who(m.requestedByUserId)}`
          : "through Forgot password"
        : `set by ${who(m.resetByUserId)}`;
    case "EMAIL_CHANGE_REQUESTED":
      return typeof m.newEmail === "string" ? `to ${m.newEmail}, by ${who(m.requestedByUserId)}` : "";
    case "EMAIL_CHANGED":
      return typeof m.oldEmail === "string" ? `was ${m.oldEmail}` : "";
    // G-149 — which system, which version, and (on a publish) what changed.
    case "AI_INSTRUCTIONS_PUBLISHED":
    case "AI_INSTRUCTIONS_ROLLED_BACK": {
      const system = typeof m.purpose === "string" ? (AI_SYSTEM_NAMES[m.purpose] ?? m.purpose) : "AI";
      const version = (n: unknown) => (n === 0 ? "text in code" : typeof n === "number" ? `version ${n}` : "?");
      if (entry.event === "AI_INSTRUCTIONS_ROLLED_BACK") {
        return `${system}: ${version(m.fromNumber)} → ${version(m.number)}`;
      }
      return `${system} ${version(m.number)}${typeof m.note === "string" ? ` · ${m.note}` : ""}`;
    }
    // G-163 — whose pay, how much, which month, and who did it.
    case "PAYROLL_PAYMENT_RECORDED":
    case "PAYROLL_PAYMENT_DELETED": {
      const month = payMonth(m.periodYear, m.periodMonth);
      const method = typeof m.paymentType === "string" ? m.paymentType.toLowerCase() : null;
      return [payTermValue(m.amountEur, "eur"), month ? `for ${month}` : null, method, `by ${who(m.byUserId)}`]
        .filter(Boolean)
        .join(" · ");
    }
    case "PAYROLL_ADJUSTMENT_RECORDED":
    case "PAYROLL_ADJUSTMENT_DELETED": {
      const month = payMonth(m.periodYear, m.periodMonth);
      const kind = m.kind === "BONUS" ? "Bonus" : m.kind === "DEDUCTION" ? "Deduction" : null;
      return [
        [kind, payTermValue(m.amountEur, "eur")].filter(Boolean).join(" "),
        month ? `for ${month}` : null,
        typeof m.reason === "string" ? m.reason : null,
        `by ${who(m.byUserId)}`,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case "PAY_TERMS_CHANGED": {
      const changes =
        m.changes && typeof m.changes === "object" && !Array.isArray(m.changes)
          ? (m.changes as Record<string, { from?: unknown; to?: unknown }>)
          : {};
      const parts = Object.entries(changes).map(([field, change]) => {
        const spec = PAY_TERM_FIELDS[field] ?? { label: field, unit: "day" as const };
        return `${spec.label} ${payTermValue(change.from, spec.unit)} → ${payTermValue(change.to, spec.unit)}`;
      });
      const from = payMonth(m.salaryFromYear, m.salaryFromMonth);
      if (from) parts.push(`salary from ${from}`);
      if (m.atCreation === true) parts.push("at creation");
      parts.push(`by ${who(m.byUserId)}`);
      return parts.join(" · ");
    }
    // G-170 — which door, off → on, and who.
    case "COMMISSION_RULE_CHANGED": {
      const source = typeof m.source === "string" ? orderSourceLabel(m.source as OrderSource) : "?";
      return `${source}: ${onOff(m.from ?? false)} → ${onOff(m.to)} · by ${who(m.byUserId)}`;
    }
    // G-170 — the rule's amounts at birth; later only its name, default and active flags.
    case "COURIER_PAYOUT_RULE_CREATED": {
      const snap =
        m.snapshot && typeof m.snapshot === "object" && !Array.isArray(m.snapshot)
          ? (m.snapshot as Record<string, unknown>)
          : {};
      const amount =
        snap.kind === "PERCENT"
          ? `${percentText(Number(snap.percent))} %`
          : snap.kind === "FLAT"
            ? money(Number(snap.flatEur))
            : snap.kind === "PER_KM"
              ? `${money(Number(snap.ratePerKmEur))}/km${snap.baseFeeEur != null ? ` + ${money(Number(snap.baseFeeEur))}` : ""}`
              : null;
      const demoted =
        m.demotedDefault && typeof m.demotedDefault === "object" && !Array.isArray(m.demotedDefault)
          ? (m.demotedDefault as { name?: unknown }).name
          : null;
      return [
        typeof snap.name === "string" ? snap.name : null,
        [typeof snap.kind === "string" ? (PAYOUT_RULE_KINDS[snap.kind] ?? snap.kind) : null, amount].filter(Boolean).join(" "),
        snap.minPayoutEur != null ? `min ${money(Number(snap.minPayoutEur))}` : null,
        snap.isDefault === true ? "default" : null,
        typeof demoted === "string" ? `replaces ${demoted} as default` : null,
        `by ${who(m.byUserId)}`,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case "COURIER_PAYOUT_RULE_CHANGED": {
      const changes =
        m.changes && typeof m.changes === "object" && !Array.isArray(m.changes)
          ? (m.changes as Record<string, { from?: unknown; to?: unknown }>)
          : {};
      const parts = Object.entries(changes).map(([field, change]) => {
        const label = PAYOUT_RULE_FIELDS[field] ?? field;
        const value = (v: unknown) => (typeof v === "boolean" ? (v ? "yes" : "no") : plainValue(v));
        return `${label} ${value(change.from)} → ${value(change.to)}`;
      });
      const demoted =
        m.demotedDefault && typeof m.demotedDefault === "object" && !Array.isArray(m.demotedDefault)
          ? (m.demotedDefault as { name?: unknown }).name
          : null;
      return [
        typeof m.name === "string" ? m.name : null,
        ...parts,
        typeof demoted === "string" ? `replaces ${demoted} as default` : null,
        `by ${who(m.byUserId)}`,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    default:
      return "";
  }
}

/**
 * G-54 — the Catalog tab. These rows are about THINGS (items, templates)
 * rather than shops or accounts, so the sentence order flips: the entity
 * name is the subject, the actor is the trailing "by X".
 */
const CATALOG_ACTION_LABELS: Record<CatalogAuditAction, string> = {
  ITEM_CREATED: "Item created",
  ITEM_UPDATED: "Item edited",
  ITEM_ARCHIVED: "Item archived",
  ITEM_RESTORED: "Item restored",
  DRAFT_FINALIZED: "Item created from staging",
  TEMPLATE_CREATED: "Template created",
  TEMPLATE_UPDATED: "Template edited",
  ACTION_UNDONE: "Change undone",
  PAPER_DELETED: "Supplier paper deleted",
  // G-131: the factura ↔ albarán link and its repairs.
  PAPER_LINKED: "Factura linked",
  PAPER_UNLINKED: "Factura unlinked",
  PAPER_RELEASED: "Paper freed by a cancelled delivery",
  FACTURA_WAIT_DISMISSED: "Wait for a factura closed",
  DELIVERY_RECEIVE_UNDONE: "Delivery receiving undone",
  DELIVERY_DOCUMENT_ADDED: "Delivery document added",
  DELIVERY_DOCUMENT_DELETED: "Delivery document deleted",
  PAPER_DETAILS_TYPED: "Factura number or date typed",
  DELIVERY_MOVED: "Delivery moved to another shop",
  FACTURA_WAIT_REOPENED: "Wait for a factura reopened",
  // G-170 (WO-E part 3): the money edits.
  LOT_CORRECTED: "Lot corrected",
  CATEGORY_CREATED: "Category created",
  CATEGORY_PRICING_CHANGED: "Category IVA rate or margin changed",
  RUNNING_COST_CREATED: "Running cost added",
  RUNNING_COST_CHANGED: "Running cost changed",
  RUNNING_COST_DELETED: "Running cost deleted",
  EXPENSE_CREATED: "One-off expense added",
  EXPENSE_CHANGED: "One-off expense changed",
  EXPENSE_DELETED: "One-off expense deleted",
  // G-167 (OD-283): the office placing a web order with a shop.
  SHOPIFY_ORDER_ASSIGNED: "Web order assigned to a shop",
  SHOPIFY_ORDER_PASSED: "Web order passed to another shop",
};

export function catalogActionLabel(action: CatalogAuditAction, source?: CatalogAuditSource): string {
  // G-129: the app created the item itself when a paper was read.
  if (action === "DRAFT_FINALIZED" && source === "PAPER_AUTO") {
    return "Item created automatically from a paper";
  }
  return CATALOG_ACTION_LABELS[action] ?? humanize(action);
}

/** Archiving and an undo are the destructive-ish verbs here — amber, never red. */
export function catalogActionTone(action: CatalogAuditAction): ActionTone {
  return action === "ITEM_ARCHIVED" ||
    action === "ACTION_UNDONE" ||
    action === "PAPER_DELETED" ||
    action === "PAPER_UNLINKED" ||
    action === "DELIVERY_RECEIVE_UNDONE" ||
    action === "DELIVERY_DOCUMENT_DELETED" ||
    action === "DELIVERY_MOVED" ||
    action === "FACTURA_WAIT_REOPENED" ||
    // G-170: money changed after the fact, or a cost row gone.
    action === "LOT_CORRECTED" ||
    action === "CATEGORY_PRICING_CHANGED" ||
    action === "RUNNING_COST_DELETED" ||
    action === "EXPENSE_DELETED"
    ? "warning"
    : "neutral";
}

/**
 * G-115: what the Catalog tab can reverse (owner ruling OD-111). A creation
 * is deleted (refused by the server once stock, a sale, a delivery, a recipe
 * or a link hangs off it); an archive/restore is flipped back. Edits and
 * templates are corrected on their own pages, where all of it is visible.
 */
export const CATALOG_UNDOABLE_ACTIONS: ReadonlySet<CatalogAuditAction> =
  new Set(["ITEM_CREATED", "DRAFT_FINALIZED", "ITEM_ARCHIVED", "ITEM_RESTORED"]);

/** Friendly names for the diff's field keys. */
const CATALOG_FIELD_LABELS: Record<string, string> = {
  name: "name",
  sku: "SKU",
  barcode: "barcode",
  defaultPurchaseUnitCostEur: "purchase cost",
  defaultSaleUnitPriceEur: "selling price",
  reorderThreshold: "reorder threshold",
  defaultUom: "unit",
  isActive: "active",
  diameterCm: "diameter",
  depthCm: "depth",
  category: "category",
  color: "color",
  subCategory: "flower type",
  description: "description",
  linesReplaced: "recipe lines",
  invoiceNumber: "number",
  invoiceDate: "date",
  baseEur: "base",
  ivaEur: "IVA",
  grandTotalEur: "total",
  ivaRates: "IVA by rate",
  // G-170: a lot's facts.
  invoicedQty: "billed",
  receivedQty: "received",
  usableQty: "usable",
  remainingQty: "remaining",
  invoiceTotalEur: "total (net)",
  purchaseUnitCostEur: "unit cost (net)",
  costSource: "cost",
  moneyBasis: "typed as",
  ivaRatePercent: "IVA rate",
  grossTotalEur: "total (gross)",
  supplier: "supplier",
  purchasedAt: "bought",
  receivedAt: "received on",
  // G-170: a category's rates, a cost's and an expense's facts.
  vatRatePercent: "IVA rate",
  targetMarginPercent: "target margin",
  shop: "shop",
  monthlyAmountEur: "monthly amount",
  amountEur: "amount",
  activeFrom: "from",
  activeTo: "until",
  incurredOn: "day",
  notes: "notes",
};

/** G-170: the facts a cost or expense row keeps, as one line. */
function costSnapshotSummary(s: Record<string, unknown>): string {
  const amount = s.monthlyAmountEur ?? s.amountEur;
  return [
    typeof amount === "number" ? money(amount) : null,
    typeof s.category === "string" ? s.category.toLowerCase().replace(/_/g, " ") : null,
    typeof s.shop === "string" ? s.shop : null,
    typeof s.activeFrom === "string" ? `from ${s.activeFrom}${typeof s.activeTo === "string" ? ` until ${s.activeTo}` : ""}` : null,
    typeof s.incurredOn === "string" ? `on ${s.incurredOn}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function plainValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

/**
 * One line saying what actually changed: `name: ROSA → Red rose · barcode: — → 8412…`.
 * For creations the snapshot collapses to the interesting bits; machine
 * creations say where they came from.
 */
export function catalogSummary(entry: CatalogAuditEntry): string {
  const m =
    entry.metadata &&
    typeof entry.metadata === "object" &&
    !Array.isArray(entry.metadata)
      ? (entry.metadata as Record<string, unknown>)
      : {};

  if (entry.action === "ACTION_UNDONE") {
    const effect =
      m.effect === "ITEM_DELETED"
        ? typeof m.draftsReturned === "number" && m.draftsReturned > 0
          ? "item removed, draft back in staging"
          : "item removed"
        : m.effect === "ITEM_RESTORED"
          ? "item restored"
          : m.effect === "ITEM_ARCHIVED"
            ? "item archived again"
            : "";
    const reason = typeof m.reason === "string" ? m.reason : "";
    return [effect, reason ? `"${reason}"` : ""].filter(Boolean).join(" · ");
  }

  // G-137 (OD-184): which file, at which shop — and why, for a deletion.
  if (entry.action === "DELIVERY_DOCUMENT_ADDED" || entry.action === "DELIVERY_DOCUMENT_DELETED") {
    const reason = typeof m.reason === "string" ? m.reason : "";
    return [
      typeof m.fileName === "string" ? m.fileName : "",
      typeof m.shop === "string" ? m.shop : "",
      reason ? `"${reason}"` : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }

  // G-164 (KI-165): why the wait was reopened, and the closure it undid.
  if (entry.action === "FACTURA_WAIT_REOPENED") {
    const closed =
      m.closed && typeof m.closed === "object" && !Array.isArray(m.closed)
        ? (m.closed as { by?: unknown; reason?: unknown })
        : {};
    return [
      typeof m.reason === "string" ? `"${m.reason}"` : "",
      typeof closed.by === "string" ? `closed by ${closed.by}` : "",
      typeof closed.reason === "string" ? `"${closed.reason}"` : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }

  // G-164 (D-85): from which shop to which, and the paper that moved with it.
  if (entry.action === "DELIVERY_MOVED") {
    const paper =
      m.paper && typeof m.paper === "object" && !Array.isArray(m.paper)
        ? (m.paper as { label?: unknown }).label
        : null;
    // KI-166: the factura that bills it — moved with it, or where it stayed.
    const factura =
      m.factura && typeof m.factura === "object" && !Array.isArray(m.factura)
        ? (m.factura as { label?: unknown; moved?: unknown; stayedIn?: unknown })
        : null;
    const facturaText =
      factura && typeof factura.label === "string"
        ? factura.moved === true
          ? `factura ${factura.label} moved too`
          : typeof factura.stayedIn === "string"
            ? `factura ${factura.label} stays in ${factura.stayedIn}`
            : ""
        : "";
    return [
      typeof m.fromShop === "string" && typeof m.toShop === "string" ? `${m.fromShop} → ${m.toShop}` : "",
      typeof paper === "string" ? `paper ${paper}` : "",
      facturaText,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  // G-167 (OD-283): which shop the office gave a web order to, or moved it between, and why.
  if (entry.action === "SHOPIFY_ORDER_ASSIGNED") {
    return typeof m.shop === "string" ? `to ${m.shop}` : "";
  }
  if (entry.action === "SHOPIFY_ORDER_PASSED") {
    return [
      typeof m.from === "string" && typeof m.to === "string" ? `${m.from} → ${m.to}` : "",
      typeof m.reason === "string" ? `"${m.reason}"` : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }

  // G-170: what a cost or expense row was, when it was added or deleted.
  if (
    entry.action === "RUNNING_COST_CREATED" ||
    entry.action === "RUNNING_COST_DELETED" ||
    entry.action === "EXPENSE_CREATED" ||
    entry.action === "EXPENSE_DELETED"
  ) {
    const snapshot =
      m.snapshot && typeof m.snapshot === "object" && !Array.isArray(m.snapshot)
        ? (m.snapshot as Record<string, unknown>)
        : {};
    return costSnapshotSummary(snapshot);
  }

  // G-170: a category is born with its two rates.
  if (entry.action === "CATEGORY_CREATED") {
    const snapshot =
      m.snapshot && typeof m.snapshot === "object" && !Array.isArray(m.snapshot)
        ? (m.snapshot as Record<string, unknown>)
        : {};
    return [
      typeof snapshot.vatRatePercent === "number" ? `IVA ${percentText(snapshot.vatRatePercent)} %` : null,
      typeof snapshot.targetMarginPercent === "number" ? `target margin ${percentText(snapshot.targetMarginPercent)} %` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (entry.source === "PAPER_AUTO") {
    return typeof m.nameRaw === "string" && m.nameRaw !== entry.entityName
      ? `created by the app from the paper's line "${m.nameRaw}"`
      : "created by the app when the paper was read";
  }

  if (entry.source === "INVOICE_INGEST") {
    return "auto-created from a supplier invoice (unmatched line)";
  }

  const changes = m.changes;
  if (changes && typeof changes === "object" && !Array.isArray(changes)) {
    const parts = Object.entries(changes as Record<string, unknown>).map(
      ([field, change]) => {
        const c =
          change && typeof change === "object" && !Array.isArray(change)
            ? (change as { from?: unknown; to?: unknown })
            : {};
        if (field === "linesReplaced") {
          return `recipe replaced (${plainValue(c.to)} lines)`;
        }
        return `${CATALOG_FIELD_LABELS[field] ?? field}: ${plainValue(c.from)} → ${plainValue(c.to)}`;
      },
    );
    // G-170: a lot correction carries the reason the person gave.
    if (entry.action === "LOT_CORRECTED" && typeof m.reason === "string" && m.reason) {
      parts.push(`"${m.reason}"`);
    }
    return parts.join(" · ");
  }

  if (entry.action === "DRAFT_FINALIZED" && typeof m.nameRaw === "string") {
    return m.nameRaw !== entry.entityName
      ? `from invoice line "${m.nameRaw}"`
      : "approved from staging";
  }

  const snapshot = m.snapshot;
  if (snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) {
    const s = snapshot as Record<string, unknown>;
    const bits: string[] = [];
    if (typeof s.category === "string") bits.push(s.category);
    if (s.barcode) bits.push(`barcode ${String(s.barcode)}`);
    if (s.defaultSaleUnitPriceEur != null)
      bits.push(`€${String(s.defaultSaleUnitPriceEur)}`);
    if (s.hasPhoto === true) bits.push("with photo");
    if (typeof s.lineCount === "number") bits.push(`${s.lineCount} lines`);
    return bits.join(" · ");
  }

  return "";
}
